import { RoleListRow } from 'types/api/roles';
import { UserListRow } from 'types/api/users';

/** section id -> action bitmask. The shape /auth/me returns as `permissions`. */
export type PermissionMap = Record<number | string, number | string>;

/**
 * "Nobody may grant what they do not hold."
 *
 * Mirrors libs/auth/src/helpers/permission-ceiling.helper.ts on the backend.
 * Keep the two identical: this one decides what the panel OFFERS, that one
 * decides what the API ACCEPTS. If they disagree, the operator either sees a
 * control that 403s or loses one they are entitled to.
 *
 * Both sides are coerced with Number() because the masks are `bigint`
 * columns. The backend now serves them as numbers, but a deployed-older API
 * still sends strings and `"2" & "030"` silently never matches.
 */
export const sectionsExceedingCeiling = (
  actor: PermissionMap | undefined | null,
  target: PermissionMap
): number[] => {
  const held = actor ?? {};
  const exceeded: number[] = [];

  Object.entries(target).forEach(([section, targetMask]) => {
    const sectionId = Number(section);
    const heldMask = Number(held[sectionId] ?? held[section] ?? 0);

    if ((Number(targetMask) & ~heldMask) !== 0) exceeded.push(sectionId);
  });

  return exceeded;
};

export const isWithinCeiling = (
  actor: PermissionMap | undefined | null,
  target: PermissionMap
): boolean => sectionsExceedingCeiling(actor, target).length === 0;

/** True when every bit of `mask` is one the actor holds in that section. */
export const canGrantSection = (
  actor: PermissionMap | undefined | null,
  section: number,
  mask: number | string
): boolean => isWithinCeiling(actor, { [section]: mask });

/** The half of a role row these refusals read. */
export type RoleActionFlags = Pick<
  RoleListRow,
  'canAct' | 'canActReason' | 'canAssign' | 'canAssignReason'
>;

/** The half of an admin row adminActionRefusal reads. */
export type AdminActionFlags = Pick<UserListRow, 'canAct' | 'canActReason'>;

/**
 * Only reached when the server said no and sent no sentence with it, which
 * RolePolicy never does. Worth keeping so a refusal is never silent.
 */
const DEFAULT_REFUSAL = 'You cannot act on this role.';

/** As DEFAULT_REFUSAL, for the admins list. */
const DEFAULT_ADMIN_REFUSAL = "You cannot change this admin's role.";

/**
 * Why the operator may not edit or delete this role — '' when they may.
 *
 * THE ANSWER COMES FROM THE SERVER. Every row of GET /roles carries `canAct`
 * and `canActReason`, decided by the same RolePolicy object the write routes
 * enforce, so a control we leave enabled cannot 403 and one we disable cannot
 * have been allowed.
 *
 * This replaced canActOnRole(), a client-side mirror of the same rule that
 * only existed because the decision was not on the wire. Two implementations
 * of one rule had to be kept in step by hand, and ours could not get the
 * system-role case right on its own merits: super_admin holds ZERO permission
 * rows — its power is synthesised at login — so its stored set is empty, and
 * an empty set is a subset of everything. A section-map comparison says "you
 * may act on super_admin". Only a separate isSystem guard stopped it. Do not
 * reintroduce the mirror; ask the server.
 *
 * NOT TOLD IS NOT NO. `canAct` is absent on an API deployed before this
 * shipped, and that resolves to '' — the control stays enabled and the API
 * answers with a 403 that says why. Hiding a control the operator is entitled
 * to is the worse failure; a 403 at least explains itself.
 */
export const roleEditRefusal = (role: RoleActionFlags): string => {
  if (role.canAct !== false) return '';

  return role.canActReason || DEFAULT_REFUSAL;
};

/**
 * Why the operator may not ASSIGN this role — '' when they may.
 *
 * A SEPARATE FLAG, not a derivation from canAct. Assigning and editing are
 * the same test but for one case: a system role may never be edited by
 * anyone, a superadmin included, yet a superadmin must still be able to HAND
 * IT OUT or super_admin could never be granted again. We used to derive that
 * here as `isSuperAdmin || canAct`, which was correct against the policy as
 * it stood and would have gone silently wrong the day the two actions
 * diverged anywhere else — the picker would simply have started offering
 * something the API refuses. The server answers both questions now.
 */
export const roleAssignRefusal = (role: RoleActionFlags): string => {
  if (role.canAssign !== false) return '';

  return role.canAssignReason || DEFAULT_REFUSAL;
};

/**
 * Why the operator may not change THIS ADMIN's role — '' when they may.
 *
 * A DIFFERENT AND STRICTER QUESTION than the one a role row answers. A role's
 * `canAct` is a subset test — everything it grants, you hold — and A PEER
 * PASSES IT. Acting on a person is strictly-below: they hold nothing you do
 * not, and you hold something they do not. Eighteen people hold `test`; under
 * the subset test each could demote the other seventeen, which is the whole
 * point of the rule. Peers, incomparable pairs (permissions are a lattice,
 * not a ladder) and the operator themselves are all refused, and a superadmin
 * is exempt.
 *
 * This replaced a client-side copy of strictly-below fed by a second fetch of
 * every role — the panel resolved each holder's permission map from the roles
 * list because the user row carried only role_id. The row answers it now.
 */
export const adminActionRefusal = (admin: AdminActionFlags): string => {
  if (admin.canAct !== false) return '';

  return admin.canActReason || DEFAULT_ADMIN_REFUSAL;
};
